import { setUsername, setAccessCode, setBoostCode } from '../auth/userActions';
import { showFullLeaderboardModal } from '../ui/leaderboard';
import { showAppLoader, hideAppLoader } from './loader';

// Generous to allow uniqueness check + save + one retry on a slow server.
const USERNAME_TIMEOUT_MS = 20000;

function withTimeout(promise, ms, message) {
    return new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(message)), ms);
        promise.then(
            v => { clearTimeout(t); resolve(v); },
            e => { clearTimeout(t); reject(e); }
        );
    });
}

function hideAllModals() {
    document.getElementById('usernameModal').style.display = 'none';
    document.getElementById('accessCodeModal').style.display = 'none';
    document.getElementById('boostCodeModal').style.display = 'none';
    document.getElementById('fullLeaderboardModal').style.display = 'none';
    document.getElementById('overlay').style.display = 'none';
}

function showUsernameModal(opts) {
    hideAllModals();
    const usernameModal = document.getElementById('usernameModal');
    usernameModal.style.display = 'flex';
    const input = document.getElementById('usernameInput');
    if (input) {
        if (opts && opts.prefillCurrent) {
            const display = document.getElementById('usernameDisplay');
            input.value = (display && display.textContent.trim()) || '';
        } else {
            input.value = '';
        }
        setTimeout(() => {
            input.focus();
            if (opts && opts.prefillCurrent) input.select();
        }, 50);
    }
    const err = document.getElementById('usernameError');
    if (err) err.textContent = '';
    const button = document.getElementById('saveUsernameButton');
    if (button) button.disabled = false;
    // Fire a no-op request through the proxy so the backend cold-starts
    // while the user is typing, not when they hit Continue.
    warmBackend();
}

function warmBackend() {
    try {
        // OPTIONS won't trigger auth and is cheap; just touches the function.
        fetch('/api/user/username-check', { method: 'OPTIONS' }).catch(() => {});
    } catch (_) {}
}

function showAccessCodeModal() {
    hideAllModals();
    const accessCodeModal = document.getElementById('accessCodeModal');
    accessCodeModal.style.display = 'flex';
    const input = document.getElementById('accessCodeInput');
    if (input) {
        input.value = '';
        setTimeout(() => input.focus(), 50);
    }
}

function showBoostModal() {
    hideAllModals();
    const boostCodeModal = document.getElementById('boostCodeModal');
    const overlay = document.getElementById('overlay');
    boostCodeModal.style.display = 'block';
    overlay.style.display = 'block';
    const input = document.getElementById('boostCodeInput');
    if (input) {
        input.value = '';
        setTimeout(() => input.focus(), 50);
    }
}

function isAccessCodeModalOpen() {
    const m = document.getElementById('accessCodeModal');
    return m && m.style.display !== 'none' && m.style.display !== '';
}

const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;

function setUsernameError(message) {
    const err = document.getElementById('usernameError');
    if (err) err.textContent = message || '';
}

async function submitUsername(button) {
    const input = document.getElementById('usernameInput');
    const value = (input && input.value || '').trim();
    setUsernameError('');
    if (!value) {
        setUsernameError('Please enter a username.');
        if (input) input.focus();
        return;
    }
    if (!USERNAME_RE.test(value)) {
        setUsernameError('3–20 characters: letters, numbers, or underscore.');
        if (input) input.focus();
        return;
    }
    if (button) button.disabled = true;
    showAppLoader('Checking username…', 'username');
    try {
        await withTimeout(
            setUsername(value),
            USERNAME_TIMEOUT_MS,
            'Server is taking too long — please try again.'
        );
    } catch (err) {
        console.error('submitUsername error:', err);
        hideAppLoader();
        setUsernameError(err && err.message || 'Could not save username.');
        if (button) button.disabled = false;
        if (input) input.focus();
    }
}

function submitAccessCode() {
    const input = document.getElementById('accessCodeInput');
    const value = (input && input.value || '').trim();
    if (!value) {
        if (input) input.focus();
        return;
    }
    setAccessCode(value);
}

function submitBoostCode() {
    const input = document.getElementById('boostCodeInput');
    const value = (input && input.value || '').trim();
    if (!value) {
        if (input) input.focus();
        return;
    }
    setBoostCode(value).then(() => hideAllModals()).catch(err => {
        console.error('Error setting boost code:', err);
    });
}

function wireModalListeners() {
    const saveUsername = document.getElementById('saveUsernameButton');
    const usernameInput = document.getElementById('usernameInput');
    if (saveUsername) {
        saveUsername.addEventListener('click', () => submitUsername(saveUsername));
    }
    if (usernameInput) {
        usernameInput.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitUsername(saveUsername);
            }
        });
    }

    const saveAccess = document.getElementById('saveAccessCodeButton');
    const accessInput = document.getElementById('accessCodeInput');
    if (saveAccess) saveAccess.addEventListener('click', submitAccessCode);
    if (accessInput) {
        accessInput.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitAccessCode();
            }
        });
    }

    const saveBoost = document.getElementById('saveBoostCodeButton');
    const boostInput = document.getElementById('boostCodeInput');
    if (saveBoost) saveBoost.addEventListener('click', submitBoostCode);
    if (boostInput) {
        boostInput.addEventListener('keydown', e => {
            if (e.key === 'Enter') {
                e.preventDefault();
                submitBoostCode();
            }
        });
    }

    document.querySelectorAll('.close-button').forEach(btn => {
        btn.addEventListener('click', () => {
            if (isAccessCodeModalOpen()) return;
            hideAllModals();
        });
    });

    const overlay = document.getElementById('overlay');
    if (overlay) {
        overlay.addEventListener('click', () => {
            if (isAccessCodeModalOpen()) return;
            hideAllModals();
        });
    }

    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && isAccessCodeModalOpen()) {
            e.stopPropagation();
            e.preventDefault();
        }
    }, true);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireModalListeners);
} else {
    wireModalListeners();
}

export { hideAllModals, showUsernameModal, showAccessCodeModal, showBoostModal, showFullLeaderboardModal };
